import React, { useRef } from 'react';
import {
  useForm,
  SubmitHandler,
  FieldErrors,
  FormProvider,
} from 'react-hook-form';
import {
  Grid,
  GridItem,
  Heading,
} from '@chakra-ui/react';

import { PaymentElementProps, PaymentElementResponse } from '../../../@types';
import { SupportedGateways } from '../../../constants';
import { CardInputControl } from '../../ui-elements/CardInputControl';
import { InputControl } from '../../ui-elements/InputControl';
import { StripeInputControl } from '../../ui-elements/StripeInputControl';
import { CheckboxControl } from '../../ui-elements/CheckboxControl';
import { SelectControl } from '../../ui-elements/SelectControl';
import { countryList } from '../../../utils/countries';
import { PaymentProvider } from '../../providers/Provider';
import { useCommonOptionsStore } from '../../../store';
import { useTranslations } from '../../../translations/useTranslations';

import { CreditCardFormProps } from './creditCard.types';
import { CreditCardFormPropTypes } from './creditCard.prop-types';

type FormInputsType = Record<string, any>;
type CreditCardDefaultProps = PaymentElementProps['guest'] & { cardholder: string };

const layoutProps: Record<string, any> = {
  gap: 6,
  columnGap: 4,
};

const initFields: CreditCardDefaultProps = {
  firstName: '',
  lastName: '',
  email: '',
  cardholder: '',
  billingDetails: {
    billingAddress: '',
    billingState: '',
    billingCity: '',
    billingZip: '',
    billingCountry: '',
    billingEmail: '',
  },
};

const guestToBilling = (
  guestData: PaymentElementProps['guest'],
) => ({
  ...guestData?.billingDetails,
});

const repackFormData = (data: any) => {
  const billingDetails = Object.fromEntries(
    Object.keys(data)
      .filter(key => key.startsWith('billing'))
      .map(key => [key, data[key]]),
  );
  const { cardNumber, cardholder, expiry, cvc, firstName, lastName } = data;
  return {
    firstName,
    lastName,
    ...((typeof cardNumber === 'string') && { cardNumber: cardNumber.replace(/ /g, '') }),
    cardholder,
    // eslint-disable-next-line no-unsafe-optional-chaining
    ...((typeof expiry === 'string') && { expMonth: +expiry?.split('/')[0]?.trim() || 0 }),
    // eslint-disable-next-line no-unsafe-optional-chaining
    ...((typeof expiry === 'string') && { expYear: 2000 + +expiry?.split('/')[1]?.trim() || 0 }),
    ...((typeof cvc === 'string') && { cvc }),
    billingDetails,
  };
};

const flattenDefaultValues = (props: CreditCardDefaultProps) => {
  const {
    billingDetails, ...rest
  } = props;
  return {
    ...rest, ...billingDetails,
  };
};

const sanitizeErrors = (errors: FieldErrors<FormInputsType>) => (
  Object.fromEntries(Object.entries(errors).map(([key, val]) => ([key, val?.message])))
);

const unpackErrorMessage = (
  errors: FieldErrors<FormInputsType>,
  name: string,
) => (errors[name] ? errors[name]?.message ? errors[name]?.message : true : false);

const CreditCardForm = React.forwardRef(({
  gateway,
  onChange,
  onLoadStart,
  onLoadEnd,
  onSuccess,
  onError,
  onSubmit,
  guest,
  billingConfig,
  contactInformationConfig,
}: CreditCardFormProps, forwardRef: any) => {
  const paymentProviderRef = useRef<any>();
  const stripeTheme = useCommonOptionsStore(state => state.stripeTheme);

  const lang = useCommonOptionsStore(state => state.lang);
  const [t] = useTranslations(lang);

  const formMethods = useForm<FormInputsType>({
    mode: 'onChange',
    defaultValues: {
      ...flattenDefaultValues({
        ...initFields, ...guest,
      }),
      cardholder: `${guest?.firstName} ${guest?.lastName}`.trim(),
    },
  });

  const { handleSubmit, formState, getValues, setValue, trigger } = formMethods;

  const {
    errors,
    isLoading,
    isSubmitSuccessful,
    isSubmitting,
  } = formState;

  const errCount = React.useMemo(() => (errors ? Object.keys(errors).length : 0), [errors]);
  const hasBillingConfig = React
    .useMemo(() => Boolean(billingConfig && Object.keys(billingConfig).length), [billingConfig]);

  React.useEffect(() => {
    if (onLoadStart && isLoading) onLoadStart();
    if (onLoadEnd && isLoading) onLoadEnd();
  }, [isLoading, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onLoadStart && isSubmitting) onLoadStart();
    if (onLoadEnd && isSubmitting) onLoadEnd();
  }, [isSubmitting, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onSuccess && isSubmitSuccessful) onSuccess();
  }, [isSubmitSuccessful, onSuccess]);

  React.useEffect(() => {
    if (onError && errCount) onError(sanitizeErrors(errors));
  }, [onError, errors, errCount]);

  const onFieldChange = (fieldName: string) => {
    if (onChange) onChange({ [fieldName]: getValues(fieldName) });
    if (onError && errors[fieldName]) onError({ [fieldName]: errors[fieldName]?.message });
  };

  const onSameAsAddressChange = (isChecked: any) => {
    const billingFields = isChecked ? guestToBilling(guest) : { ...initFields.billingDetails };
    Object.entries(billingFields).forEach(([key, val]) => {
      setValue(key, val);
    });
  };

  const submitHandler: SubmitHandler<FormInputsType> = async (formData: any) => {
    const data = repackFormData(formData);
    const token = await paymentProviderRef.current.processPayment(data);

    if (onSubmit) {
      const responseData: PaymentElementResponse = {
        ...data,
        ...(token?.id && { token: token.id }),
        paymentMethod: 'cards',
      };

      onSubmit({ ...responseData });
    }
  };

  const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
    await paymentProviderRef.current.test('submitErrorHandler');
    if (onError) onError(sanitizeErrors(errors));
  };

  React.useImperativeHandle(forwardRef, () => ({
    submit() {
      handleSubmit(submitHandler, submitErrorHandler)();
    },
    async validate() {
      return trigger();
    },
  }));

  return (
    <>
      <PaymentProvider gateway={gateway} ref={paymentProviderRef} />
      <FormProvider {...formMethods} >
        <div>
          {contactInformationConfig && <Heading
            className="section-heading"
            paddingTop={(layoutProps as any).gap}
            paddingBottom={(layoutProps as any).gap}
          >{t('contactInformation')}</Heading>}

          {contactInformationConfig && <Grid
            templateColumns="repeat(4, 1fr)"
            templateRows="repeat(auto-fill, 1fr)"
            gap={(layoutProps as any).gap}
            columnGap={(layoutProps as any).columnGap}
          >
            {contactInformationConfig?.firstName && <GridItem colSpan={2}>
              <InputControl
                name="firstName"
                label={t('firstName')}
                error={unpackErrorMessage(errors, 'firstName')}
                onChange={onFieldChange}
                isRequired={contactInformationConfig?.firstName?.required}
              />
            </GridItem>}
            {contactInformationConfig?.lastName && <GridItem colSpan={2}>
              <InputControl
                name="lastName"
                label={t('lastName')}
                error={unpackErrorMessage(errors, 'lastName')}
                onChange={onFieldChange}
                isRequired={contactInformationConfig?.lastName?.required}
              />
            </GridItem>}
          </Grid>}

          <Heading
            className="section-heading"
            paddingTop={(layoutProps as any).gap}
            paddingBottom={(layoutProps as any).gap}
          >{t('paymentDetails')}</Heading>

          <Grid
            templateColumns="repeat(4, 1fr)"
            templateRows="repeat(auto-fill, 1fr)"
            gap={(layoutProps as any).gap}
            columnGap={(layoutProps as any).columnGap}
          >
            <GridItem colSpan={4}>
              <InputControl
                name="cardholder"
                label={t('cardholderName')}
                error={unpackErrorMessage(errors, 'cardholder')}
                onChange={onFieldChange}
                isRequired
              />
            </GridItem>

            <GridItem colSpan={4}>
              {gateway === SupportedGateways.STRIPE && <StripeInputControl
                id="card-number"
                inputType="card"
                name="cardNumber"
                label={t('cardNumber')}
                error={unpackErrorMessage(errors, 'cardNumber')}
                onChange={onFieldChange}
                theme={stripeTheme?.cardInput}
                isRequired
              />}

              {gateway === SupportedGateways.ADYEN && <CardInputControl
                name="cardNumber"
                label={t('cardNumber')}
                error={unpackErrorMessage(errors, 'cardNumber')}
                onChange={onFieldChange}
                placeholder="1234 1234 1234 1234"
                isRequired
              />}

            </GridItem>
            <GridItem colSpan={2}>
              {gateway === SupportedGateways.STRIPE && <StripeInputControl
                id="card-expiry"
                inputType="expiry"
                name="expiry"
                label={t('cardExpire')}
                error={unpackErrorMessage(errors, 'expiry')}
                onChange={onFieldChange}
                theme={stripeTheme?.expiryInput}
                isRequired
              />}

              {gateway === SupportedGateways.ADYEN && <InputControl
                name="expiry"
                label={t('cardExpire')}
                error={unpackErrorMessage(errors, 'expiry')}
                rules={{
                  validate: {
                    valid: (val: any) => {
                      const year = (new Date().getFullYear()) % 100;
                      const month = new Date().getMonth() + 1;
                      const values = val.split('/');
                      if (+values[1] > year && +values[0] <= 12) return true;
                      if (+values[1] === year && +values[0] <= 12 && +values[0] >= month) return true;
                      return 'Invalid date';
                    },
                  },
                }}
                onChange={onFieldChange}
                placeholder="MM/YY"
                mask="00/00"
                isRequired
              />}
            </GridItem>
            <GridItem colSpan={2}>
              {gateway === SupportedGateways.STRIPE && <StripeInputControl
                id="card-cvc"
                inputType="cvc"
                name="cvc"
                label="CVC"
                error={unpackErrorMessage(errors, 'cvc')}
                onChange={onFieldChange}
                theme={stripeTheme?.cvcInput}
                isRequired
              />}

              {gateway === SupportedGateways.ADYEN && <InputControl
                name="cvc"
                label="CVC"
                error={unpackErrorMessage(errors, 'cvc')}
                rules={{
                  minLength: {
                    value: 3, message: '3 digits',
                  },
                }}
                onChange={onFieldChange}
                placeholder="000"
                mask="0000"
                isRequired
              />}
            </GridItem>
          </Grid>

          {hasBillingConfig && <Heading
            className="section-heading"
            paddingTop={(layoutProps as any).gap}
            paddingBottom={(layoutProps as any).gap}
          >{t('billingAddressDetails')}</Heading>}

          {hasBillingConfig && <Grid
            templateColumns="repeat(2, 1fr)"
            templateRows="repeat(auto-fill, 1fr)"
            gap={(layoutProps as any).gap}
            columnGap={(layoutProps as any).columnGap}
          >
            {guest && <GridItem colSpan={2}>
              <CheckboxControl
                name="fill_billing_details"
                label={t('sameAsAddress')}
                onChange={onSameAsAddressChange}
              />
            </GridItem>}
            {billingConfig?.billingAddress && <GridItem colSpan={2}>
              <InputControl
                name="billingAddress"
                label={t('billingStreetAddress')}
                error={unpackErrorMessage(errors, 'billingAddress')}
                onChange={onFieldChange}
                isRequired={billingConfig?.billingAddress?.required}
              />
            </GridItem>}
            {billingConfig?.billingAddress2 && <GridItem colSpan={2}>
              <InputControl
                name="billingAddress2"
                label={t('billingStreetAddress2')}
                error={unpackErrorMessage(errors, 'billingAddress2')}
                onChange={onFieldChange}
                isRequired={billingConfig?.billingAddress2?.required}
              />
            </GridItem>}
            {billingConfig?.billingCountry && <GridItem colSpan={2}>
              <SelectControl
                name="billingCountry"
                label={t('billingCountry')}
                error={unpackErrorMessage(errors, 'billingCountry')}
                onChange={onFieldChange}
                list={countryList}
                isRequired={billingConfig?.billingCountry?.required}
              />
            </GridItem>}
            {billingConfig?.billingState && <GridItem>
              <InputControl
                name="billingState"
                label={t('billingState')}
                error={unpackErrorMessage(errors, 'billingState')}
                onChange={onFieldChange}
                isRequired={billingConfig?.billingState?.required}
              />
            </GridItem>}
            {billingConfig?.billingCity && <GridItem>
              <InputControl
                name="billingCity"
                label={t('billingCity')}
                error={unpackErrorMessage(errors, 'billingCity')}
                onChange={onFieldChange}
                isRequired={billingConfig?.billingCity?.required}
              />
            </GridItem>}
            {billingConfig?.billingZip && <GridItem>
              <InputControl
                name="billingZip"
                label={t('billingZip')}
                error={unpackErrorMessage(errors, 'billingZip')}
                onChange={onFieldChange}
                isRequired={billingConfig?.billingZip?.required}
              />
            </GridItem>}
            {billingConfig?.billingEmail && <GridItem colSpan={2}>
              <InputControl
                name="billingEmail"
                label={t('billingEmail')}
                error={unpackErrorMessage(errors, 'billingEmail')}
                onChange={onFieldChange}
                rules={{
                  validate: (val: any) => (
                    val && Array.isArray(val.match(/[^\s@]+@[^\s@]+\.[^\s@]+/gi))
                  ) || t('invalidEmail'),
                }}
                isRequired={billingConfig?.billingEmail?.required}
              />
            </GridItem>}
          </Grid>}
        </div>
      </FormProvider>
    </>
  );
});

CreditCardForm.propTypes = CreditCardFormPropTypes;

export { CreditCardForm };
